import { useModal } from '../../../helpers/hooks/modalContext';
import { TransactionType, type WalletHistoryItem, type rentCreditResponse } from '../../../helpers/utils/types';

type RentCreditHistorySectionProps = {
  loading: boolean;
  items: WalletHistoryItem[];
  stats: rentCreditResponse;
};

const formatDollars = (value: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(Math.abs(value));

const HomeIcon = () => (
  <svg
    width='16'
    height='16'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    <path d='M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2z' />
    <polyline points='9 22 9 12 15 12 15 22' />
  </svg>
);

const DollarIcon = () => (
  <svg
    width='16'
    height='16'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
    aria-hidden='true'
  >
    <line x1='12' y1='1' x2='12' y2='23' />
    <path d='M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6' />
  </svg>
);

const isCredit = (item: WalletHistoryItem) =>
  item.rentCreditTransactionType === TransactionType.Credit;

const isDebit = (item: WalletHistoryItem) =>
  item.rentCreditTransactionType === TransactionType.Debit;

const getHistoryVisual = (item: WalletHistoryItem) => {
  if (isCredit(item)) {
    return { iconClass: 'gold', icon: <DollarIcon />, titleClass: 'rd-ph-title-gold' };
  }

  return { iconClass: 'blue', icon: <HomeIcon />, titleClass: '' };
};

const getStatusChip = (item: WalletHistoryItem) => {
  if (item.isReverted) {
    return <span className='rd-ph-badge gray'>Reverted</span>;
  }

  if (isCredit(item)) {
    return <span className='rd-ph-badge earned'>Earned</span>;
  }

  if (isDebit(item)) {
    return <span className='rd-ph-badge confirmed'>Applied</span>;
  }

  return null;
};

export const RentCreditHistorySection = ({
  loading,
  items,
  stats,
}: RentCreditHistorySectionProps) => {
  const { setModal } = useModal();

  const recentItems = items.slice(0, 4);

  return (
    <div className='rd-panel-light'>
      <div className='rd-sec'>
        <div>
          <h2 className='rd-sec-title'>Rent credit history</h2>
          <p className='rd-sec-sub'>Recent wallet activity.</p>
        </div>
        <button
          type='button'
          className='rd-view-all'
          onClick={() => setModal('rewards', { openTab: 'history', openSection: 'wallet' })}
        >
          View all
        </button>
      </div>

      <div className='rd-ph'>
        {loading ? (
          <div className='rd-ph-empty'>Loading rent credit history...</div>
        ) : recentItems.length === 0 ? (
          <div className='rd-ph-empty'>No rent credit history available yet.</div>
        ) : (
          recentItems.map((item) => {
            const visual = getHistoryVisual(item);
            const credit = isCredit(item);
            const debit = isDebit(item);
            const amountClass = credit ? 'green' : debit ? 'red' : 'gray';
            const amountPrefix = credit ? '+' : debit ? '−' : '';
            const title = item.note || 'Rent credit activity';
            const subtitle =
              item.cardCompanyTypeValue && item.cardLast4Digits
                ? `${item.cardCompanyTypeValue} ···· ${item.cardLast4Digits}`
                : null;

            return (
              <div key={item.rentCreditTransactionId} className='rd-ph-row'>
                <div className={`rd-ph-ic ${visual.iconClass}`}>{visual.icon}</div>
                <div className='rd-ph-main'>
                  <div className={`rd-ph-title ${visual.titleClass}`.trim()}>{title}</div>
                  <div className='rd-ph-date'>
                    {subtitle ? `${subtitle} · ` : ''}
                    {new Date(item.timestampAdded).toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    })}
                  </div>
                </div>
                <div className='rd-ph-right'>
                  <div className={`rd-ph-amt ${amountClass}`}>
                    {amountPrefix}
                    {formatDollars(Number(item.amount ?? 0))}
                  </div>
                  {getStatusChip(item)}
                </div>
              </div>
            );
          })
        )}
      </div>

      {!loading && items.length > 0 && (
        <div className='rd-ph-summary'>
          <div className='rd-ph-summary-item'>
            <span className='rd-ph-summary-lbl'>Total earned</span>
            <span className='rd-ph-summary-val green'>
              +{formatDollars(Number(stats.totalRentCredit ?? 0))}
            </span>
          </div>
          <div className='rd-ph-summary-sep' />
          <div className='rd-ph-summary-item'>
            <span className='rd-ph-summary-lbl'>Total redeemed</span>
            <span className='rd-ph-summary-val red'>
              {Number(stats.totalRedeemed ?? 0) > 0 ? '−' : ''}
              {formatDollars(Number(stats.totalRedeemed ?? 0))}
            </span>
          </div>
          <div className='rd-ph-summary-sep' />
          <div className='rd-ph-summary-item'>
            <span className='rd-ph-summary-lbl'>Available</span>
            <span className='rd-ph-summary-val'>
              {formatDollars(Number(stats.totalAvailable ?? 0))}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};
